import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card } from "../../../../ui/components/Card";
import { Callout } from "../../../../ui/components/Callout";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { useAuth } from "../../../lib/auth/hooks";
import * as userApi from "../../../lib/api/users";
import { getApiErrorMessage } from "../../../lib/api/api-error";
import {
  clearSignupOnboarding,
  getPersistedUserSetupTask,
  persistUserSetupTask,
  startSignupOnboarding,
} from "../../../lib/auth/signup-onboarding";
import {
  getFirstIncompleteTask,
  getSetupProgress,
  getSkipTargetTask,
  isPhoneCodeExpired,
  isPhoneSetupComplete,
  isTaskComplete,
  type SetupTask,
} from "../../../lib/auth/user-setup-progress";
import { UserSetupNavigation } from "../../user-setup/UserSetupNavigation";
import { UserSetupFooter } from "../../user-setup/UserSetupFooter";
import { EmailSetupStep } from "../../user-setup/EmailSetupStep";
import { PhoneSetupStep } from "../../user-setup/PhoneSetupStep";
import { OrganizationSetupStep } from "../../user-setup/OrganizationSetupStep";
import { useEmailVerification } from "../../profile/useEmailVerification";
import {
  hasSetupSearchCode,
  normalizeSetupSearchCode,
  type UserSetupSearch,
} from "../../../lib/auth/user-setup-search";
import {
  getPendingExternalRedirectApp,
  returnAfterAuth,
} from "../../../lib/auth/external-redirect";
import { acceptPendingProductInvite } from "../../../lib/auth/accept-pending-product-invite";
import { clearInviteToken } from "../../../lib/auth/invite-pending";
import {
  getProAppOrigin,
  hardNavigateOnce,
} from "../../../lib/auth/app-origins";
import { useI18n } from "../../../lib/i18n";

export interface UserSetupContentProps {
  /** When rendered as a modal, called to close it. When absent, behaves as full-page. */
  onClose?: () => void;
  /** Initial tab to show (overrides route search). */
  initialTab?: SetupTask;
  /** Hide the brand header and footer (useful inside modal). */
  compact?: boolean;
  /** Route search params — passed in by the page wrapper when used as a full page. */
  routeSearch?: UserSetupSearch;
}

/**
 * Core user-setup widget. Usable both as a standalone page and inside a modal.
 */
export function UserSetupContent({
  onClose,
  initialTab,
  compact = false,
  routeSearch,
}: UserSetupContentProps) {
  const navigate = useNavigate();
  const { user, accessToken, refreshUser, logout } = useAuth();
  const { t } = useI18n();

  const isModal = Boolean(onClose);
  const search: UserSetupSearch = routeSearch ?? { tab: initialTab };

  const taskResolvedFromProfile = React.useRef(false);
  const [orgStubVersion, setOrgStubVersion] = React.useReducer((x: number) => x + 1, 0);

  const [activeTask, setActiveTask] = React.useState<SetupTask>(() => {
    if (hasSetupSearchCode(search)) return "email";
    if (search.tab) return search.tab;
    const persisted = getPersistedUserSetupTask();
    if (persisted) return persisted;
    return getFirstIncompleteTask(user, search);
  });

  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const displayEmail = user?.email ?? search.email ?? "your@email.com";
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const emailVerification = useEmailVerification({
    active: activeTask === "email",
    search,
    requestOnOpen: false,
    onManualCodeVerify: isModal
      ? undefined
      : (code) => {
          navigate({
            to: "/user-setup",
            search: { email: displayEmail, code, tab: "email" },
            replace: true,
          });
        },
    onClearVerification: isModal
      ? undefined
      : () => {
          if (hasSetupSearchCode(search)) {
            navigate({ to: "/user-setup", search: { tab: "email" }, replace: true });
          }
        },
  });

  const clearSetupSessionState = React.useCallback(() => {
    setError(null);
  }, []);

  const selectTask = React.useCallback(
    (task: SetupTask) => {
      if (task === activeTask) return;
      clearSetupSessionState();
      setActiveTask(task);
      if (!isModal) {
        persistUserSetupTask(task);
        navigate({ to: "/user-setup", search: { tab: task }, replace: true });
      }
    },
    [activeTask, clearSetupSessionState, navigate, isModal],
  );

  React.useEffect(() => {
    if (!isModal) startSignupOnboarding();
  }, [isModal]);

  React.useEffect(() => {
    if (isModal) return;
    if (hasSetupSearchCode(search)) {
      setActiveTask("email");
      persistUserSetupTask("email");
      return;
    }
    if (search.tab) {
      setActiveTask((prev) => {
        if (prev !== search.tab) {
          clearSetupSessionState();
        }
        return search.tab!;
      });
      persistUserSetupTask(search.tab);
    }
  }, [search.code, search.tab, clearSetupSessionState, isModal]);

  React.useEffect(() => {
    if (isModal) return;
    if (!user || taskResolvedFromProfile.current) return;
    if (hasSetupSearchCode(search)) return;

    taskResolvedFromProfile.current = true;
    if (!search.tab) {
      const persisted = getPersistedUserSetupTask();
      const next = persisted ?? getFirstIncompleteTask(user, search);
      setActiveTask(next);
      persistUserSetupTask(next);
    }
  }, [user, search.code, search.tab, user?.emailVerified, user?.phoneVerified, user?.hasOrganizationMembership, orgStubVersion, isModal]);

  React.useEffect(() => {
    const code = normalizeSetupSearchCode(search.code);
    if (!code || code.length !== 6) return;
    setActiveTask("email");
    persistUserSetupTask("email");
  }, [search.code]);

  React.useEffect(() => {
    if (isModal || !user?.emailVerified) return;
    if (!hasSetupSearchCode(search)) return;
    navigate({ to: "/user-setup", search: { tab: "email" }, replace: true });
  }, [isModal, user?.emailVerified, search.code, navigate]);

  const handleFinish = async () => {
    if (isModal) {
      await refreshUser();
      onClose?.();
      return;
    }
    if (!token) {
      setError(t("account.common.sessionExpired"));
      return;
    }
    setLoading(true);
    try {
      await userApi.completeProfileSetup(token);
      const productId = await acceptPendingProductInvite(token);
      clearInviteToken();
      await refreshUser();
      clearSignupOnboarding();
      if (productId) {
        hardNavigateOnce(
          `${getProAppOrigin()}/workspace/products/${productId}`,
        );
        return;
      }
      if (!returnAfterAuth()) {
        navigate({ to: "/", replace: true });
      }
    } catch (e) {
      setError(getApiErrorMessage(e, t("account.setup.errorCompleteFailed")));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (isModal) {
      onClose?.();
      return;
    }
    if (activeTask === "organization") {
      void handleFinish();
      return;
    }
    const next = getSkipTargetTask(activeTask, user, search);
    if (next && next !== activeTask) selectTask(next);
    else void handleFinish();
  };

  const handleLogout = async () => {
    await logout();
    navigate({ to: "/login", replace: true });
  };

  const taskComplete = React.useCallback(
    (task: SetupTask) => isTaskComplete(task, user, search),
    [user, search],
  );

  const { percent } = getSetupProgress(user, search);
  // Whenever a step's Skip button would be hidden (task already complete —
  // e.g. an org-membership request is pending), fall back to Done instead of
  // showing nothing.
  const showSkip = !taskComplete(activeTask);
  const showDone = !showSkip;
  const goToTarget =
    getPendingExternalRedirectApp() === "certification" ? "trainings" : "dashboard";
  const nextAfterEmail = getSkipTargetTask("email", user, search);
  const nextAfterPhone = getSkipTargetTask("phone", user, search);

  const sectionErrorMessage =
    error ??
    (activeTask === "email" ? emailVerification.sectionError : null) ??
    (activeTask === "phone" && !isPhoneSetupComplete(user) && isPhoneCodeExpired(user)
      ? t("account.setup.codeExpiredError")
      : null);

  const bodyContent = (
    <>
      <div className={authText.userSetupScrollBody}>
        {activeTask === "email" && (
          <EmailSetupStep
            user={emailVerification.user}
            search={emailVerification.search}
            displayEmail={emailVerification.displayEmail}
            verifyStatus={emailVerification.verifyStatus}
            verifyMessage={emailVerification.verifyMessage}
            resendLoading={emailVerification.resendLoading}
            onResendEmail={emailVerification.handleResendEmail}
            onManualCodeVerify={emailVerification.handleManualCodeVerify}
            onClearVerification={emailVerification.clearEmailVerificationState}
            onContinueToTask={selectTask}
            nextTask={nextAfterEmail}
          />
        )}
        {activeTask === "phone" && (
          <PhoneSetupStep
            user={user}
            token={token}
            loading={loading}
            onError={setError}
            onLoading={setLoading}
            onRefreshUser={refreshUser}
            onContinueToTask={selectTask}
            nextTask={nextAfterPhone}
          />
        )}
        {activeTask === "organization" && (
          <OrganizationSetupStep
            user={user}
            search={search}
            displayEmail={displayEmail}
            onPendingChange={() => {
              setOrgStubVersion();
              void refreshUser();
            }}
          />
        )}

        {sectionErrorMessage && (
          <div className={authText.setupSectionError}>
            <Callout variant="error" centered>
              {sectionErrorMessage}
            </Callout>
          </div>
        )}
      </div>
    </>
  );

  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <UserSetupNavigation
          activeTask={activeTask}
          progressPercent={percent}
          isTaskComplete={taskComplete}
          onSelectTask={selectTask}
        />
        {bodyContent}
      </div>
    );
  }

  return (
    <Card maxWidth={560} className={authText.userSetupCard}>
      <div className={authText.userSetupStickyTop}>
        <AuthBrand />
        <UserSetupNavigation
          activeTask={activeTask}
          progressPercent={percent}
          isTaskComplete={taskComplete}
          onSelectTask={selectTask}
        />
      </div>

      {bodyContent}
      <div className={authText.userSetupStickyBottom}>
        <UserSetupFooter
          showSkip={showSkip}
          showDone={showDone}
          skipLoading={loading}
          doneLoading={loading}
          dashboardLoading={loading}
          onSkip={handleSkip}
          onDone={() => void handleFinish()}
          onGoToDashboard={handleFinish}
          onLogout={handleLogout}
          goToTarget={goToTarget}
        />
      </div>
    </Card>
  );
}

/** Full-page wrapper that uses route search params. */
export function UserSetupPage({ search }: { search: UserSetupSearch }) {
  return <UserSetupContent routeSearch={search} />;
}
